import { ConversationStatus } from '@/types/pulse';
import { Survey, SurveyWithStats } from '@/types/pulsedb';
import { SupabaseClient } from '@supabase/supabase-js';
import { cache } from 'react';

export const getSurveysWithStats = cache(
  async (
    supabase: SupabaseClient,
    workspaceId: string,
    limit?: number
  ): Promise<SurveyWithStats[] | null> => {
    let query = supabase
      .from('surveys')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('deleted', false)
      .order('id', { ascending: false })
      .throwOnError();

    if (limit) query = query.limit(limit);
    const { data: surveys } = await query;

    if (!surveys || surveys.length === 0) return [];

    const surveyIds = surveys.map((s: Survey) => s.id);

    const { data: stats } = await supabase
      .from('survey_responses')
      .select('survey_id, ended_at')
      .in('survey_id', surveyIds)
      .eq('status', ConversationStatus.COMPLETED)
      .eq('deleted', false)
      .order('ended_at', { ascending: false })
      .throwOnError();

    const statsMap = new Map<
      number,
      { last_completed_at: string | null; completed_count: number }
    >();

    if (stats) {
      for (const stat of stats) {
        const existing = statsMap.get(stat.survey_id);
        if (existing) {
          existing.completed_count++;
        } else {
          statsMap.set(stat.survey_id, {
            last_completed_at: stat.ended_at,
            completed_count: 1
          });
        }
      }
    }

    return surveys.map((survey: Survey) => ({
      ...survey,
      last_completed_at: statsMap.get(survey.id)?.last_completed_at || null,
      completed_count: statsMap.get(survey.id)?.completed_count || 0
    }));
  }
);

export const getSurveyBySlug = async (
  supabase: SupabaseClient,
  slug: string
): Promise<Survey | null> => {
  const { data } = await supabase
    .from('surveys')
    .select('*')
    .eq('slug', slug)
    .eq('deleted', false)
    .maybeSingle()
    .throwOnError();
  return data;
};

export const getSurvey = async (
  supabase: SupabaseClient,
  id: number
): Promise<Survey | null> => {
  const resp = await supabase
    .from('surveys')
    .select('*')
    .eq('id', id)
    .eq('deleted', false)
    .single()
    .throwOnError();
  return resp.data;
};

export const getSurveyWorkspaceId = async (
  supabase: SupabaseClient,
  id: number
): Promise<string | null> => {
  const resp = await supabase
    .from('surveys')
    .select('workspace_id')
    .eq('id', id)
    .eq('deleted', false)
    .single()
    .throwOnError();
  return resp.data?.workspace_id ?? null;
};
